/**
 * Which columns the ruled A4 invoice prints.
 *
 * ── Why this is a setting and not a layout decision ─────────────────────────
 * Rule 46 of the CGST Rules fixes a floor, not a format. A tax invoice must
 * carry the description of the goods, the HSN/SAC, the quantity with its unit
 * and the value of the supply — everything past that is the trade's own
 * convention, and the conventions differ. A hardware counter wants the rate
 * and the unit; a wholesaler wants the line discount; a shop billing at a
 * single slab wants neither and would rather have the width.
 *
 * So the four statutory columns cannot be switched off — they are marked
 * `mandatory` here and the Settings screen renders them as fixed — and
 * everything else is the merchant's call.
 *
 * The order is FIXED, and deliberately so: a reader scans an invoice by shape,
 * and a merchant who could reorder the columns could produce something that
 * reads like an invoice but is not the document their buyer expects. Turning a
 * column off closes the gap; it does not shuffle the rest.
 *
 * This mirrors RECEIPT_CONFIG, which does the same job for the thermal
 * receipt — same shape of setting, same merge-over-defaults reading, so an
 * unknown or half-written value degrades to the default instead of to a blank
 * invoice.
 */

import type { ReceiptLineItem } from '../contracts/ReceiptContracts';

export type A4ColumnId =
  | 'sl'
  | 'description'
  | 'hsn'
  | 'quantity'
  | 'rate'
  | 'per'
  | 'discount'
  | 'gstRate'
  | 'gstAmount'
  | 'amount'
  | 'lineTotal';

export interface A4ColumnDef {
  id: A4ColumnId;
  /** Column heading. `<br/>` is allowed — these are narrow columns. */
  label: string;
  /** One line in Settings saying what it is for. */
  hint: string;
  /**
   * Required by Rule 46. Cannot be switched off, and the Settings screen shows
   * it as fixed rather than as a toggle that refuses to move.
   */
  mandatory?: boolean;
  /** On for a new store. */
  defaultOn: boolean;
  /** Cell alignment class: c = centre, r = right, omitted = left. */
  align?: 'c' | 'r';
  /**
   * Width as a percentage of the table. Description takes whatever is left, so
   * it has no width of its own.
   */
  width?: number;
  /** Emphasis on the cell, matching the existing bold Quantity/Amount. */
  bold?: boolean;
}

/**
 * Every column the template can print, in the order it prints them.
 *
 * `amount` is the TAXABLE value of the line, not the tax-inclusive total —
 * the invoice prints one Amount column and then lists CGST/SGST beneath it, so
 * that column has to be the figure the tax is added to. `lineTotal` is the
 * inclusive one, for merchants who want each line to show what it came to.
 */
export const A4_COLUMNS: readonly A4ColumnDef[] = [
  {
    id: 'sl',
    label: 'Sl<br/>No.',
    hint: 'Line number down the left edge.',
    defaultOn: true,
    align: 'c',
    width: 5,
  },
  {
    id: 'description',
    label: 'Description of Goods',
    hint: 'What was sold. Required on a tax invoice.',
    mandatory: true,
    defaultOn: true,
  },
  {
    id: 'hsn',
    label: 'HSN/SAC',
    hint: 'Classification code. Required on a tax invoice.',
    mandatory: true,
    defaultOn: true,
    align: 'c',
    width: 11,
  },
  {
    id: 'quantity',
    label: 'Quantity',
    hint: 'How many, with the unit. Required on a tax invoice.',
    mandatory: true,
    defaultOn: true,
    align: 'r',
    width: 12,
    bold: true,
  },
  {
    id: 'rate',
    label: 'Rate',
    hint: 'Price of one unit before tax.',
    defaultOn: true,
    align: 'r',
    width: 11,
  },
  {
    id: 'per',
    label: 'per',
    hint: 'The unit the rate is quoted in — PCS, KG, MTR.',
    defaultOn: true,
    align: 'c',
    width: 6,
  },
  {
    id: 'discount',
    label: 'Disc<br/>%',
    hint: 'Discount given on the line, as a percentage.',
    defaultOn: false,
    align: 'r',
    width: 7,
  },
  {
    id: 'gstRate',
    label: 'GST<br/>%',
    hint: 'Tax slab for the line. Also shown in the summary below.',
    defaultOn: false,
    align: 'r',
    width: 7,
  },
  {
    id: 'gstAmount',
    label: 'GST<br/>Amount',
    hint: 'Tax charged on the line. Also shown in the summary below.',
    defaultOn: false,
    align: 'r',
    width: 11,
  },
  {
    id: 'amount',
    label: 'Amount',
    hint: 'Taxable value of the line. Required on a tax invoice.',
    mandatory: true,
    defaultOn: true,
    align: 'r',
    width: 13,
    bold: true,
  },
  {
    id: 'lineTotal',
    label: 'Total',
    hint: 'What the line comes to including tax.',
    defaultOn: false,
    align: 'r',
    width: 13,
    bold: true,
  },
];

const BY_ID = new Map(A4_COLUMNS.map((c) => [c.id, c]));

/** The statutory four. Switching these off is not a preference. */
export const MANDATORY_COLUMNS: readonly A4ColumnId[] = A4_COLUMNS.filter((c) => c.mandatory).map(
  (c) => c.id,
);

/** What a store prints before anybody changes anything. */
export function defaultA4Columns(): A4ColumnId[] {
  return A4_COLUMNS.filter((c) => c.defaultOn).map((c) => c.id);
}

/**
 * Read a stored selection back into a usable one.
 *
 * Unknown ids are dropped (a column removed in a later version), the mandatory
 * four are forced back in however the value was written, and the result is put
 * back into the canonical order — so a hand-edited or partially-synced setting
 * still prints a lawful invoice rather than an unreadable one.
 */
export function parseA4Columns(raw: string | null | undefined): A4ColumnId[] {
  let wanted: string[] | null = null;
  if (raw && raw.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) wanted = parsed.filter((v): v is string => typeof v === 'string');
    } catch {
      wanted = null;
    }
  }
  if (!wanted) return defaultA4Columns();

  const chosen = new Set(wanted.filter((id) => BY_ID.has(id as A4ColumnId)));
  for (const id of MANDATORY_COLUMNS) chosen.add(id);
  return A4_COLUMNS.filter((c) => chosen.has(c.id)).map((c) => c.id);
}

export function serializeA4Columns(ids: A4ColumnId[]): string {
  const chosen = new Set<string>(ids);
  for (const id of MANDATORY_COLUMNS) chosen.add(id);
  return JSON.stringify(A4_COLUMNS.filter((c) => chosen.has(c.id)).map((c) => c.id));
}

/**
 * The column definitions for a selection, in print order.
 *
 * The statutory four are added back here as well as in `parseA4Columns`,
 * because this is the function the template actually renders from. Enforcing
 * it only at the parse step would mean any caller passing a hand-built array —
 * a test, a future export, a caller that forgot — could print an invoice
 * missing a Rule 46 particular.
 */
export function resolveA4Columns(ids: A4ColumnId[]): A4ColumnDef[] {
  const chosen = new Set<A4ColumnId>(ids);
  for (const id of MANDATORY_COLUMNS) chosen.add(id);
  return A4_COLUMNS.filter((c) => chosen.has(c.id));
}

/**
 * Width for each column, as percentages summing to 100.
 *
 * Every column but Description has a fixed width; Description absorbs the rest,
 * because it is the one holding prose. It is floored at 18% so that a merchant
 * who turns everything on still gets a readable product name rather than a
 * column two characters wide.
 */
export function columnWidths(cols: A4ColumnDef[]): number[] {
  const fixed = cols.reduce((sum, c) => sum + (c.id === 'description' ? 0 : (c.width ?? 0)), 0);
  const descWidth = Math.max(18, 100 - fixed);
  // Turning on every optional column can overrun 100; scale the fixed ones
  // back so the row still adds up instead of spilling off the page.
  const overflow = fixed + descWidth - 100;
  const scale = overflow > 0 ? (fixed - overflow) / fixed : 1;
  return cols.map((c) =>
    c.id === 'description' ? descWidth : Math.max(4, (c.width ?? 0) * scale),
  );
}

/** What goes in a cell, given the line. Pure — the template does the escaping. */
export interface CellContext {
  index: number;
  /** Taxable value of the line, already resolved by the template. */
  taxable: number;
  /** Formatted money, so this file holds no formatting rules of its own. */
  num: (n: number) => string;
  qtyWithUnit: (qty: number, unit?: string) => string;
}

export function cellValue(
  id: A4ColumnId,
  item: ReceiptLineItem,
  ctx: CellContext,
): string {
  switch (id) {
    case 'sl':
      return String(ctx.index + 1);
    case 'description':
      return item.name;
    case 'hsn':
      return item.hsn ?? '';
    case 'quantity':
      return ctx.qtyWithUnit(item.qty, item.unit);
    case 'rate':
      return ctx.num(item.rate);
    case 'per':
      return item.unit ? item.unit.toUpperCase() : '';
    case 'discount':
      // A line with no discount prints nothing, not "0.00" — an empty cell
      // reads as "none given", a zero reads as a discount that was calculated.
      return item.discountPct ? `${item.discountPct}%` : '';
    case 'gstRate':
      return typeof item.gstRate === 'number' ? `${item.gstRate}%` : '';
    case 'gstAmount':
      return typeof item.taxAmount === 'number' ? ctx.num(item.taxAmount) : '';
    case 'amount':
      return ctx.num(ctx.taxable);
    case 'lineTotal':
      return ctx.num(item.amount);
  }
}
